<template>
  <transition name="pt-drawer">
    <div v-if="visible" class="pt-drawer-mask" @click.self="$emit('close')">
      <aside class="pt-drawer" @click.stop>
        <header class="pt-drawer__header">
          <h3 class="pt-drawer__title">{{ $t ? $t('PT 발표 설정') : 'PT 발표 설정' }}</h3>
          <button class="pt-drawer__close" @click="$emit('close')">✕</button>
        </header>

        <div class="pt-drawer__body">
          <!-- 발표 제목 -->
          <section class="pt-drawer__section">
            <label class="pt-drawer__label">{{ $t ? $t('발표 제목') : '발표 제목' }}</label>
            <input
              type="text"
              class="pt-drawer__input"
              v-model="local.title"
              :placeholder="$t ? $t('예) 안전 공지 — 2026-05-27') : '예) 안전 공지'"
            />
          </section>

          <!-- 모드 — 카드형 선택 -->
          <section class="pt-drawer__section">
            <label class="pt-drawer__label">{{ $t ? $t('통역 모드') : '통역 모드' }}</label>
            <div class="pt-mode-cards">
              <!-- 신속 모드 (STT + 번역) -->
              <button
                type="button"
                class="pt-mode-card"
                :class="{ 'pt-mode-card--active': local.ptMode === 'stt' }"
                @click="local.ptMode = 'stt'"
              >
                <div class="pt-mode-card__head">
                  <span class="pt-mode-card__icon">⚡</span>
                  <span class="pt-mode-card__title">{{ $t ? $t('신속 모드') : '신속 모드' }}</span>
                  <span class="pt-mode-card__radio" :class="{ 'pt-mode-card__radio--on': local.ptMode === 'stt' }"></span>
                </div>
                <p class="pt-mode-card__summary">
                  {{ $t ? $t('발화하는 즉시 짧은 구간씩 번역해 표시합니다.') : '발화하는 즉시 짧은 구간씩 번역해 표시합니다.' }}
                </p>
                <ul class="pt-mode-card__pros">
                  <li>{{ $t ? $t('실시간성이 가장 좋음') : '실시간성이 가장 좋음' }}</li>
                  <li>{{ $t ? $t('짧은 발화·즉각 반응에 유리') : '짧은 발화·즉각 반응에 유리' }}</li>
                </ul>
                <ul class="pt-mode-card__cons">
                  <li>{{ $t ? $t('짧은 단위 번역이라 문맥상 부정확할 수 있음') : '짧은 단위 번역이라 문맥상 부정확할 수 있음' }}</li>
                </ul>
                <p class="pt-mode-card__usecase">
                  💡 {{ $t ? $t('긴급 공지, 짧은 안내, 즉각 이해가 중요한 경우') : '긴급 공지, 짧은 안내, 즉각 이해가 중요한 경우' }}
                </p>
              </button>

              <!-- 정확도 모드 (Live × N) -->
              <button
                type="button"
                class="pt-mode-card"
                :class="{
                  'pt-mode-card--active': local.ptMode === 'live',
                  'pt-mode-card--disabled': cellsCountForGate > 6,
                }"
                :disabled="cellsCountForGate > 6"
                @click="cellsCountForGate <= 6 && (local.ptMode = 'live')"
              >
                <div class="pt-mode-card__head">
                  <span class="pt-mode-card__icon">🎯</span>
                  <span class="pt-mode-card__title">{{ $t ? $t('정확도 모드') : '정확도 모드' }}</span>
                  <span class="pt-mode-card__radio" :class="{ 'pt-mode-card__radio--on': local.ptMode === 'live' }"></span>
                </div>
                <p class="pt-mode-card__summary">
                  {{ $t ? $t('AI가 한 문장을 듣고 분석한 뒤 자연스럽게 통역합니다.') : 'AI가 한 문장을 듣고 분석한 뒤 자연스럽게 통역합니다.' }}
                </p>
                <ul class="pt-mode-card__pros">
                  <li>{{ $t ? $t('문맥을 반영한 자연스러운 번역') : '문맥을 반영한 자연스러운 번역' }}</li>
                  <li>{{ $t ? $t('동시통역사와 가까운 품질') : '동시통역사와 가까운 품질' }}</li>
                </ul>
                <ul class="pt-mode-card__cons">
                  <li>{{ $t ? $t('한 문장 끝날 때마다 표시 (수 초 지연)') : '한 문장 끝날 때마다 표시 (수 초 지연)' }}</li>
                  <li>{{ $t ? $t('격자 수에 비례한 비용 (최대 6격자)') : '격자 수에 비례한 비용 (최대 6격자)' }}</li>
                </ul>
                <p class="pt-mode-card__usecase">
                  💡 {{ $t ? $t('정식 발표, 안전 교육, 정확한 의미 전달이 필요한 공지') : '정식 발표, 안전 교육, 정확한 의미 전달이 필요한 공지' }}
                </p>
                <p v-if="cellsCountForGate > 6" class="pt-mode-card__gate-warn">
                  ⚠ {{ $t ? $t('격자가 6개를 초과하여 정확도 모드를 선택할 수 없습니다.') : '격자가 6개를 초과하여 정확도 모드를 선택할 수 없습니다.' }}
                </p>
              </button>
            </div>
          </section>

          <!-- 발표자 언어 -->
          <section class="pt-drawer__section">
            <label class="pt-drawer__label">{{ $t ? $t('발표자 언어') : '발표자 언어' }}</label>
            <select class="pt-drawer__select" v-model="local.sourceLang">
              <option v-for="opt in langOptions" :key="'src_' + opt.code" :value="opt.code">
                {{ opt.flag }} {{ opt.label }} ({{ opt.code }})
              </option>
            </select>
          </section>

          <!-- 격자 레이아웃 -->
          <section class="pt-drawer__section">
            <label class="pt-drawer__label">{{ $t ? $t('격자 레이아웃') : '격자 레이아웃' }}</label>
            <div class="pt-drawer__layout-grid">
              <button
                v-for="opt in layoutOptions"
                :key="opt.value"
                class="pt-drawer__layout-btn"
                :class="{ 'pt-drawer__layout-btn--active': local.layout === opt.value }"
                @click="local.layout = opt.value"
              >{{ opt.label }}</button>
            </div>
          </section>

          <!-- 글로벌 폰트 스케일 -->
          <section class="pt-drawer__section">
            <label class="pt-drawer__label">{{ $t ? $t('전체 글자 크기') : '전체 글자 크기' }}</label>
            <select class="pt-drawer__select" v-model.number="local.fontScaleGlobal">
              <option v-for="opt in fontSizeOptions" :key="'fsg_' + opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
          </section>

          <!-- 발표자 미리보기 띠 -->
          <section class="pt-drawer__section">
            <label class="pt-drawer__checkbox">
              <input type="checkbox" v-model="local.showSpeakerStrip" />
              <span>{{ $t ? $t('상단에 발표자 원문 띠 표시') : '상단에 발표자 원문 띠 표시' }}</span>
            </label>
          </section>

          <!-- 격자 설정 -->
          <section class="pt-drawer__section">
            <div class="pt-drawer__section-head">
              <label class="pt-drawer__label" style="margin:0;">{{ $t ? $t('격자 설정') : '격자 설정' }} ({{ local.cells.length }}/9)</label>
              <button
                class="pt-drawer__btn pt-drawer__btn--ghost"
                @click="addCell"
                :disabled="local.cells.length >= 9"
              >+ {{ $t ? $t('추가') : '추가' }}</button>
            </div>

            <div class="pt-drawer__cells">
              <div
                v-for="(cell, idx) in local.cells"
                :key="'cell_' + idx"
                class="pt-drawer__cell-row"
                :class="{
                  'pt-drawer__cell-row--dragging': draggingIdx === idx,
                  'pt-drawer__cell-row--drag-over': dragOverIdx === idx && draggingIdx !== null && draggingIdx !== idx,
                }"
                :style="{ backgroundColor: cell.bg, color: cell.fg }"
                draggable="true"
                @dragstart="onDragStart(idx, $event)"
                @dragover="onDragOver(idx, $event)"
                @dragleave="onDragLeave(idx)"
                @drop="onDrop(idx, $event)"
                @dragend="onDragEnd"
              >
                <!-- 드래그 핸들 (두 행에 걸쳐 세로 가운데) -->
                <div class="pt-drawer__cell-handle" :title="$t ? $t('드래그하여 순서 변경') : '드래그하여 순서 변경'">⋮⋮</div>

                <!-- 1행: slot# | language select | remove -->
                <span class="pt-drawer__cell-slot">#{{ idx + 1 }}</span>
                <select class="pt-drawer__cell-lang" v-model="cell.lang" @mousedown.stop>
                  <option v-for="opt in langOptions" :key="'c_' + idx + '_' + opt.code" :value="opt.code">
                    {{ opt.flag }} {{ opt.label }}
                  </option>
                </select>
                <button class="pt-drawer__cell-remove" @click="removeCell(idx)" :title="$t ? $t('제거') : '제거'">✕</button>

                <!-- 2행: 색상칩 묶음 | font-size select -->
                <div class="pt-drawer__cell-colors">
                  <label class="pt-drawer__cell-color" :title="$t ? $t('배경색') : '배경색'" @mousedown.stop>
                    <input type="color" v-model="cell.bg" />
                  </label>
                  <label class="pt-drawer__cell-color" :title="$t ? $t('글자색') : '글자색'" @mousedown.stop>
                    <input type="color" v-model="cell.fg" />
                    <span class="pt-drawer__cell-color-aa">Aa</span>
                  </label>
                </div>
                <select
                  class="pt-drawer__cell-scale-select"
                  v-model.number="cell.fontScale"
                  :title="$t ? $t('글자 크기') : '글자 크기'"
                  @mousedown.stop
                >
                  <option v-for="opt in fontSizeOptions" :key="'cs_' + idx + '_' + opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
              </div>
            </div>
          </section>
        </div>

        <footer class="pt-drawer__footer">
          <button class="pt-drawer__btn pt-drawer__btn--ghost" @click="resetToDefault">
            {{ $t ? $t('기본값') : '기본값' }}
          </button>
          <div style="flex:1;"></div>
          <button class="pt-drawer__btn pt-drawer__btn--ghost" @click="$emit('close')">
            {{ $t ? $t('취소') : '취소' }}
          </button>
          <button class="pt-drawer__btn pt-drawer__btn--primary" @click="onSave">
            {{ $t ? $t('저장') : '저장' }}
          </button>
        </footer>
      </aside>
    </div>
  </transition>
</template>

<script>
import * as UiUtil from '@/util/UiUtil'
const LANG_OPTIONS = [
  { code: 'ko', label: '한국어', flag: '🇰🇷' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'vi', label: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'th', label: 'ไทย', flag: '🇹🇭' },
  { code: 'id', label: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'uz', label: 'O‘zbekcha', flag: '🇺🇿' },
  { code: 'ne', label: 'नेपाली', flag: '🇳🇵' },
  { code: 'mn', label: 'Монгол', flag: '🇲🇳' },
  { code: 'km', label: 'ភាសាខ្មែរ', flag: '🇰🇭' },
  { code: 'my', label: 'မြန်မာစာ', flag: '🇲🇲' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
]

const LAYOUT_OPTIONS = [
  { value: 'auto', label: '자동' },
  { value: '1x2', label: '1×2' },
  { value: '2x2', label: '2×2' },
  { value: '2x3', label: '2×3' },
  { value: '3x2', label: '3×2' },
  { value: '3x3', label: '3×3' },
]

const FONT_SIZE_OPTIONS = [
  { value: 0.6, label: '아주 작게 (60%)' },
  { value: 0.8, label: '작게 (80%)' },
  { value: 1.0, label: '보통 (100%)' },
  { value: 1.2, label: '크게 (120%)' },
  { value: 1.4, label: '아주 크게 (140%)' },
  { value: 1.7, label: '특대 (170%)' },
  { value: 2.0, label: '최대 (200%)' },
]

const CELL_PALETTE = [
  { bg: '#0F172A', fg: '#FFFFFF' },
  { bg: '#1E293B', fg: '#FEF3C7' },
  { bg: '#7F1D1D', fg: '#FEE2E2' },
  { bg: '#14532D', fg: '#D1FAE5' },
  { bg: '#1E3A8A', fg: '#DBEAFE' },
  { bg: '#581C87', fg: '#FAE8FF' },
  { bg: '#7C2D12', fg: '#FED7AA' },
  { bg: '#134E4A', fg: '#CCFBF1' },
  { bg: '#831843', fg: '#FCE7F3' },
]

function defaultCells() {
  return [
    { slot: 0, lang: 'en', bg: '#0F172A', fg: '#FFFFFF', fontScale: 1.0 },
    { slot: 1, lang: 'vi', bg: '#14532D', fg: '#D1FAE5', fontScale: 1.0 },
    { slot: 2, lang: 'zh', bg: '#7F1D1D', fg: '#FEE2E2', fontScale: 1.0 },
    { slot: 3, lang: 'th', bg: '#581C87', fg: '#FAE8FF', fontScale: 1.0 },
  ]
}

function defaultConfig() {
  return {
    title: '',
    ptMode: 'stt',
    sourceLang: 'ko',
    layout: 'auto',
    fontScaleGlobal: 1.0,
    showSpeakerStrip: true,
    // showModelBadge 는 사용자 설정 UI 에서 제거됨 (디버그용). 기본 false 로 강제.
    showModelBadge: false,
    cells: defaultCells(),
  }
}

export default {
  name: 'PtSettingsDrawer',
  props: {
    visible: { type: Boolean, default: false },
    /** 외부에서 주입되는 사용자 설정 (없으면 기본값) */
    config: { type: Object, default: null },
  },
  data() {
    return {
      local: defaultConfig(),
      langOptions: LANG_OPTIONS,
      layoutOptions: LAYOUT_OPTIONS,
      fontSizeOptions: FONT_SIZE_OPTIONS,
      draggingIdx: null,
      dragOverIdx: null,
    }
  },
  computed: {
    cellsCountForGate: function () {
      return Array.isArray(this.local.cells) ? this.local.cells.length : 0
    },
  },
  watch: {
    cellsCountForGate: function (n) {
      // 격자 추가로 6 초과 시 고속 모드 자동 해제
      if (n > 6 && this.local.ptMode === 'live') {
        this.local.ptMode = 'stt'
      }
    },
  },
  watch: {
    visible: function (v) {
      if (v) this._syncFromProps()
    },
  },
  created() {
    this._syncFromProps()
  },
  methods: {
    _syncFromProps: function () {
      if (this.config) {
        // 깊은 복사 (사용자가 취소 시 외부 객체에 영향 없도록)
        this.local = JSON.parse(JSON.stringify(this.config))
        // 누락된 키 보정
        const def = defaultConfig()
        const keys = Object.keys(def)
        for (let i = 0; i < keys.length; i++) {
          if (this.local[keys[i]] === undefined) this.local[keys[i]] = def[keys[i]]
        }
        if (!Array.isArray(this.local.cells) || this.local.cells.length === 0) {
          this.local.cells = defaultCells()
        }
      } else {
        this.local = defaultConfig()
      }
    },

    addCell: function () {
      if (this.local.cells.length >= 9) return
      const idx = this.local.cells.length
      const pal = CELL_PALETTE[idx % CELL_PALETTE.length]
      // 아직 안 쓴 lang 우선 선택
      let nextLang = 'en'
      const used = {}
      for (let i = 0; i < this.local.cells.length; i++) used[this.local.cells[i].lang] = true
      for (let i = 0; i < LANG_OPTIONS.length; i++) {
        if (!used[LANG_OPTIONS[i].code] && LANG_OPTIONS[i].code !== this.local.sourceLang) {
          nextLang = LANG_OPTIONS[i].code
          break
        }
      }
      this.local.cells.push({
        slot: idx,
        lang: nextLang,
        bg: pal.bg,
        fg: pal.fg,
        fontScale: 1.0,
      })
    },

    removeCell: function (idx) {
      this.local.cells.splice(idx, 1)
      // slot 재정렬
      for (let i = 0; i < this.local.cells.length; i++) {
        this.local.cells[i].slot = i
      }
    },

    // ─── 드래그 앤 드롭 (HTML5 native) ───────────────────────
    onDragStart: function (idx, e) {
      this.draggingIdx = idx
      try {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', String(idx))
      } catch (err) { /* ignore */ }
    },
    onDragOver: function (idx, e) {
      // preventDefault 가 있어야 drop 이벤트 발생
      if (this.draggingIdx === null) return
      e.preventDefault()
      try { e.dataTransfer.dropEffect = 'move' } catch (err) { /* ignore */ }
      if (this.dragOverIdx !== idx) this.dragOverIdx = idx
    },
    onDragLeave: function (idx) {
      if (this.dragOverIdx === idx) this.dragOverIdx = null
    },
    onDrop: function (targetIdx, e) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault()
      const src = this.draggingIdx
      this.draggingIdx = null
      this.dragOverIdx = null
      if (src === null || src === targetIdx) return
      const arr = this.local.cells
      if (src < 0 || src >= arr.length || targetIdx < 0 || targetIdx >= arr.length) return
      const moved = arr.splice(src, 1)[0]
      arr.splice(targetIdx, 0, moved)
      // slot 재정렬 (저장 시 직렬화에 사용됨)
      for (let i = 0; i < arr.length; i++) arr[i].slot = i
    },
    onDragEnd: function () {
      this.draggingIdx = null
      this.dragOverIdx = null
    },

    resetToDefault: function () {
      this.local = defaultConfig()
    },

    onSave: function () {
      // 유효성: target lang 중복 검증 (UX 측 경고)
      const seen = {}
      const dup = []
      for (let i = 0; i < this.local.cells.length; i++) {
        const l = this.local.cells[i].lang
        if (seen[l]) dup.push(l)
        else seen[l] = true
      }
      if (dup.length > 0) {
        const msg = (this.$t ? this.$t('중복된 언어가 있습니다') : '중복된 언어가 있습니다') + ': ' + dup.join(', ')
        if (this.$vs && this.$vs.notify) {
          this.$vs.notify({ title: 'PT', text: msg, color: 'warning', position: 'top-center', time: 3000 })
        } else {
          UiUtil.alertDialog(msg)
        }
        // 중복 있어도 저장은 허용 — 사용자 책임
      }
      this.$emit('save', JSON.parse(JSON.stringify(this.local)))
    },
  },
}
</script>

<style scoped>
.pt-drawer-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 10100;
  display: flex;
  justify-content: flex-end;
}

.pt-drawer {
  width: 420px;
  max-width: 95vw;
  height: 100%;
  background: #fff;
  display: grid;
  grid-template-rows: auto 1fr auto;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.2);
}

.pt-drawer__header {
  display: flex;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #E5E7EB;
}

.pt-drawer__title {
  flex: 1;
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #111827;
}

.pt-drawer__close {
  border: none;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  color: #6B7280;
}
.pt-drawer__close:hover { color: #111827; }

.pt-drawer__body {
  padding: 16px 18px;
  overflow-y: auto;
}

.pt-drawer__section {
  margin-bottom: 18px;
}

.pt-drawer__section-head {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}

.pt-drawer__label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

.pt-drawer__hint {
  margin: 6px 0 0 0;
  font-size: 12px;
  color: #6B7280;
}
.pt-drawer__hint--warn { color: #B45309; }

/* ─── 모드 카드 (신속/정확도 선택) ──────────────────────────── */
.pt-mode-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pt-mode-card {
  text-align: left;
  border: 1px solid #E5E7EB;
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s, box-shadow 0.15s;
  font-family: inherit;
  color: #1F2937;
}
.pt-mode-card:hover:not(.pt-mode-card--disabled) {
  border-color: #A5B4FC;
  background: #F8FAFF;
}
.pt-mode-card--active {
  border-color: #4338CA;
  background: #EEF2FF;
  box-shadow: 0 0 0 2px rgba(67, 56, 202, 0.15);
}
.pt-mode-card--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pt-mode-card__head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.pt-mode-card__icon {
  font-size: 18px;
  line-height: 1;
}
.pt-mode-card__title {
  flex: 1;
  font-size: 14px;
  font-weight: 700;
  color: #111827;
}
.pt-mode-card__radio {
  width: 16px;
  height: 16px;
  border: 2px solid #D1D5DB;
  border-radius: 50%;
  flex-shrink: 0;
  background: #fff;
  transition: border-color 0.15s, background-color 0.15s;
}
.pt-mode-card__radio--on {
  border-color: #4338CA;
  background: radial-gradient(circle, #4338CA 38%, #fff 42%);
}

.pt-mode-card__summary {
  margin: 0 0 8px 0;
  font-size: 12.5px;
  color: #374151;
  line-height: 1.4;
}

.pt-mode-card__pros,
.pt-mode-card__cons {
  margin: 0 0 6px 0;
  padding: 0;
  list-style: none;
  font-size: 12px;
  line-height: 1.5;
}
.pt-mode-card__pros li::before {
  content: "✓ ";
  color: #059669;
  font-weight: 700;
  margin-right: 2px;
}
.pt-mode-card__cons li::before {
  content: "✗ ";
  color: #DC2626;
  font-weight: 700;
  margin-right: 2px;
}
.pt-mode-card__pros li,
.pt-mode-card__cons li {
  color: #4B5563;
}

.pt-mode-card__usecase {
  margin: 8px 0 0 0;
  padding: 8px 10px;
  background: #FEF3C7;
  border-radius: 6px;
  font-size: 11.5px;
  color: #78350F;
  line-height: 1.4;
}

.pt-mode-card__gate-warn {
  margin: 8px 0 0 0;
  font-size: 11.5px;
  color: #B45309;
  font-weight: 600;
}

.pt-drawer__hint-inline {
  font-weight: 500;
  color: #6B7280;
  margin-left: 6px;
}

.pt-drawer__input,
.pt-drawer__select {
  width: 100%;
  padding: 8px 10px;
  font-size: 14px;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  background: #fff;
  color: #111827;
}

.pt-drawer__radio-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pt-drawer__radio,
.pt-drawer__checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #1F2937;
  cursor: pointer;
}
.pt-drawer__radio--disabled { color: #9CA3AF; cursor: not-allowed; }

.pt-drawer__layout-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.pt-drawer__layout-btn {
  padding: 8px;
  border: 1px solid #D1D5DB;
  background: #fff;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  color: #374151;
}
.pt-drawer__layout-btn--active {
  background: #4338CA;
  color: #fff;
  border-color: #4338CA;
}

.pt-drawer__range {
  width: 100%;
}

.pt-drawer__cells {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ─── 격자 카드 단일 grid ──────────────────────────────────────
   카드 전체가 하나의 grid. 1행 lang select 와 2행 scale select 가
   동일한 grid-column 3 에 위치하여 폭과 시작/끝 X 좌표가 정확히 일치.
   레이아웃:
     [핸들] [#1 ] [Language select ▼] [✕]
            [🎨🎨Aa] [폰트 크기  select ▼]
*/
.pt-drawer__cell-row {
  display: grid;
  grid-template-columns: 20px 60px 1fr 28px;
  grid-template-rows: auto auto;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  transition: opacity 0.2s, box-shadow 0.2s, transform 0.2s;
}

.pt-drawer__cell-row--dragging {
  opacity: 0.35;
}

.pt-drawer__cell-row--drag-over {
  /* 드롭 위치 표시: 카드 상단 강조 라인 */
  box-shadow: 0 -3px 0 0 #FBBF24 inset, 0 0 0 2px rgba(251, 191, 36, 0.55);
}

.pt-drawer__cell-handle {
  grid-column: 1;
  grid-row: 1 / span 2;
  align-self: center;
  justify-self: center;
  cursor: grab;
  user-select: none;
  font-size: 14px;
  line-height: 1;
  letter-spacing: -3px;
  opacity: 0.5;
  padding: 4px 2px;
}
.pt-drawer__cell-handle:hover { opacity: 1; }
.pt-drawer__cell-row--dragging .pt-drawer__cell-handle,
.pt-drawer__cell-handle:active { cursor: grabbing; }

.pt-drawer__cell-slot {
  grid-column: 2;
  grid-row: 1;
  align-self: center;
  justify-self: start;
  font-size: 13px;
  font-weight: 700;
  opacity: 0.85;
}

.pt-drawer__cell-lang {
  grid-column: 3;
  grid-row: 1;
  width: 100%;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.15);
  color: inherit;
  font-size: 13px;
}
.pt-drawer__cell-lang option { background: #1F2937; color: #fff; }

.pt-drawer__cell-remove {
  grid-column: 4;
  grid-row: 1;
  align-self: center;
  justify-self: center;
  width: 26px;
  height: 26px;
  border-radius: 5px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: inherit;
  cursor: pointer;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pt-drawer__cell-remove:hover {
  background: rgba(239, 68, 68, 0.5);
}

/* 2행: 색상칩 두 개를 col 2 에 묶음 (slot# 아래) */
.pt-drawer__cell-colors {
  grid-column: 2;
  grid-row: 2;
  align-self: center;
  justify-self: start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pt-drawer__cell-color {
  display: inline-flex;
  align-items: center;
}
.pt-drawer__cell-color input[type="color"] {
  width: 26px;
  height: 26px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 5px;
  background: none;
  cursor: pointer;
}
/* 글자색 칩 위에 Aa 오버레이 — 색상 의미를 즉시 식별 */
.pt-drawer__cell-color {
  position: relative;
}
.pt-drawer__cell-color-aa {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 10px;
  font-weight: 800;
  pointer-events: none;
  text-shadow: 0 0 2px rgba(0, 0, 0, 0.7);
  color: inherit;
}

.pt-drawer__cell-scale-select {
  grid-column: 3;
  grid-row: 2;
  width: 100%;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.15);
  color: inherit;
  font-size: 13px;
}
.pt-drawer__cell-scale-select option { background: #1F2937; color: #fff; }

.pt-drawer__footer {
  display: flex;
  gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid #E5E7EB;
  background: #F9FAFB;
}

.pt-drawer__btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.pt-drawer__btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pt-drawer__btn--ghost { background: transparent; color: #374151; border: 1px solid #D1D5DB; }
.pt-drawer__btn--ghost:hover { background: #F3F4F6; }
.pt-drawer__btn--primary { background: #4338CA; color: #fff; }
.pt-drawer__btn--primary:hover { background: #3730A3; }

/* 트랜지션 */
.pt-drawer-enter-active, .pt-drawer-leave-active { transition: opacity 0.2s; }
.pt-drawer-enter, .pt-drawer-leave-to { opacity: 0; }
</style>
